import { useState } from 'react';
import { FolderOpen, Folder, Archive, Trash2, Globe, Plus, Pencil, X, Check, LayoutGrid } from 'lucide-react';
import { toast } from 'sonner';
import { useProjectsHubStore, isActive, isArchived, isTrashed } from '../../stores/useProjectsHubStore';

const SectionLabel = ({ children }) => (
  <p className="px-2.5 pt-3 pb-1 text-[10px] uppercase tracking-[0.18em] font-semibold text-[var(--c-text-faint)]">{children}</p>
);

const NavItem = ({ k, icon: Icon, label, count, active, isOver, collapsed, folder, droppable, onSelect, onOver, onLeave, onDrop, onRename, onDelete }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const submit = () => {
    const name = draft.trim();
    if (name && name !== folder?.name) onRename(name);
    setEditing(false);
  };
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => !editing && onSelect(k)}
      onKeyDown={(e) => e.key === 'Enter' && !editing && onSelect(k)}
      onDragOver={droppable ? (e) => { e.preventDefault(); onOver(k); } : undefined}
      onDragLeave={droppable ? onLeave : undefined}
      onDrop={droppable ? (e) => { e.preventDefault(); onDrop(k); } : undefined}
      data-testid={folder ? `folder-item-${folder.folder_id}` : `filter-chip-${k}`}
      data-active={active ? '1' : '0'}
      title={collapsed ? `${label} (${count})` : undefined}
      className={`group relative flex items-center gap-2.5 ${collapsed ? 'lg:justify-center lg:px-0' : 'px-2.5'} py-1.5 rounded-md text-[12.5px] cursor-pointer select-none transition-colors border border-transparent
        ${active ? 'bg-[var(--c-gold-soft)] text-[var(--c-text)]' : 'text-[var(--c-text-2)] hover:bg-[var(--c-surface-2)] hover:text-[var(--c-text)]'}
        ${isOver ? '!border-[var(--c-gold)] border-dashed bg-[var(--c-gold-soft)]' : ''}`}
    >
      {active && <span className={`absolute ${collapsed ? 'lg:left-0' : 'left-0'} top-1.5 bottom-1.5 w-[2px] rounded-full bg-[var(--c-gold)]`} />}
      <Icon className="w-[15px] h-[15px] shrink-0" />
      {!collapsed && (editing ? (
        <input
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={submit}
          onKeyDown={(e) => { if (e.key === 'Enter') submit(); if (e.key === 'Escape') setEditing(false); }}
          onClick={(e) => e.stopPropagation()}
          data-testid={`folder-rename-input-${folder.folder_id}`}
          className="flex-1 min-w-0 bg-transparent border-b border-[var(--c-gold)] outline-none text-[12.5px] text-[var(--c-text)]"
        />
      ) : (
        <>
          <span className="flex-1 truncate">{label}</span>
          {folder && (
            <span className="hidden group-hover:flex items-center gap-0.5">
              <button type="button" onClick={(e) => { e.stopPropagation(); setDraft(folder.name); setEditing(true); }} className="p-0.5 rounded hover:text-[var(--c-gold)]" title="Renommer" data-testid={`folder-rename-${folder.folder_id}`}><Pencil className="w-3 h-3" /></button>
              <button type="button" onClick={(e) => { e.stopPropagation(); onDelete(); }} className="p-0.5 rounded hover:text-red-500" title="Supprimer le dossier (les projets sont conservés)" data-testid={`folder-delete-${folder.folder_id}`}><X className="w-3 h-3" /></button>
            </span>
          )}
          <span className={`text-[10.5px] tabular-nums ${folder ? 'group-hover:hidden' : ''} ${active ? 'text-[var(--c-gold)]' : 'text-[var(--c-text-faint)]'}`}>{count}</span>
        </>
      ))}
    </div>
  );
};

/** Rail « Projets / Dossiers » rendu dans la barre latérale globale sur /projects. Les entrées sont des cibles de dépôt. */
export const ProjectFoldersNav = ({ collapsed }) => {
  const { projects, folders, filter, setFilter, dragIds, organize, createFolder, renameFolder, deleteFolder } = useProjectsHubStore();
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState('');
  const [over, setOver] = useState(null);

  const counts = {
    all: projects.filter(isActive).length,
    published: projects.filter((p) => isActive(p) && p.is_public).length,
    archived: projects.filter(isArchived).length,
    trash: projects.filter(isTrashed).length,
  };
  const folderCount = (id) => projects.filter((p) => isActive(p) && p.folder_id === id).length;

  const onDrop = async (key) => {
    setOver(null);
    if (!dragIds.length) return;
    const [action, folderId] = key === 'all' ? ['move', null] : key === 'archived' ? ['archive', null] : key === 'trash' ? ['trash', null] : ['move', key.slice(7)];
    try {
      await organize(dragIds, action, folderId);
      toast.success(action === 'move' ? 'Projet(s) déplacé(s)' : action === 'archive' ? 'Projet(s) archivé(s)' : 'Projet(s) placé(s) dans la corbeille');
    } catch { toast.error('Action impossible'); }
  };

  const submitCreate = async () => {
    const name = draft.trim();
    setCreating(false); setDraft('');
    if (!name) return;
    try { const f = await createFolder(name); setFilter(`folder:${f.folder_id}`); toast.success(`Dossier « ${name} » créé`); } catch { toast.error('Création impossible'); }
  };

  const common = (k, droppable = true) => ({
    k, collapsed, droppable, active: filter === k, isOver: over === k && dragIds.length > 0,
    onSelect: setFilter, onOver: setOver, onLeave: () => setOver(null), onDrop,
  });

  return (
    <div className="space-y-0.5" data-testid="folders-rail">
      {!collapsed && <SectionLabel>Projets</SectionLabel>}
      <NavItem {...common('all')} icon={LayoutGrid} label="Tous les projets" count={counts.all} />
      <NavItem {...common('published', false)} icon={Globe} label="Publiés" count={counts.published} />
      <NavItem {...common('archived')} icon={Archive} label="Archivés" count={counts.archived} />
      <NavItem {...common('trash')} icon={Trash2} label="Corbeille" count={counts.trash} />

      {!collapsed && <SectionLabel>Dossiers</SectionLabel>}
      {collapsed && folders.length > 0 && <div className="my-1 mx-2 h-px bg-[var(--c-border)]" />}
      {folders.map((f) => (
        <NavItem
          key={f.folder_id}
          {...common(`folder:${f.folder_id}`)}
          icon={filter === `folder:${f.folder_id}` ? FolderOpen : Folder}
          label={f.name}
          count={folderCount(f.folder_id)}
          folder={f}
          onRename={async (name) => { try { await renameFolder(f.folder_id, name); } catch { toast.error('Renommage impossible'); } }}
          onDelete={async () => { try { await deleteFolder(f.folder_id); toast.success('Dossier supprimé, projets conservés'); } catch { toast.error('Suppression impossible'); } }}
        />
      ))}

      {creating && !collapsed ? (
        <div className="flex items-center gap-1.5 px-2.5 py-1">
          <Folder className="w-[15px] h-[15px] shrink-0 text-[var(--c-text-muted)]" />
          <input
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') submitCreate(); if (e.key === 'Escape') { setCreating(false); setDraft(''); } }}
            placeholder="Nom du dossier"
            data-testid="create-folder-input"
            className="flex-1 min-w-0 bg-transparent border-b border-[var(--c-gold)] outline-none text-[12.5px] text-[var(--c-text)] placeholder:text-[var(--c-text-faint)]"
          />
          <button type="button" onClick={submitCreate} className="p-0.5 text-[var(--c-gold)]" data-testid="create-folder-confirm"><Check className="w-3.5 h-3.5" /></button>
        </div>
      ) : (
        <button
          type="button"
          onClick={async () => {
            if (!collapsed) { setCreating(true); setDraft(''); return; }
            const name = (window.prompt('Nom du dossier') || '').trim();
            if (name) { try { const f = await createFolder(name); setFilter(`folder:${f.folder_id}`); } catch { toast.error('Création impossible'); } }
          }}
          data-testid="create-folder-btn"
          title={collapsed ? 'Nouveau dossier' : undefined}
          className={`w-full flex items-center gap-2.5 ${collapsed ? 'lg:justify-center lg:px-0' : 'px-2.5'} py-1.5 rounded-md text-[12.5px] text-[var(--c-text-muted)] hover:text-[var(--c-gold)] hover:bg-[var(--c-surface-2)] transition-colors`}
        >
          <Plus className="w-[15px] h-[15px]" />
          {!collapsed && <span>Nouveau dossier</span>}
        </button>
      )}
    </div>
  );
};
